import React from 'react';
import { Shield, AlertCircle, RefreshCw, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { useCarrierHistory } from '../../hooks/useCarrierHistory';
import type { InsuranceTimelinePeriod } from '../../types/carrierHistory';

export interface InsuranceHistoryTableProps {
  dotNumber: number;
}

function formatDate(value: string | null): string {
  if (!value) return '—';
  // 'YYYY-MM-DD' — construct as a local date to avoid UTC-midnight day shift.
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  const date = match
    ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
    : new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(amount);
}

function formatCoverageAmount(period: InsuranceTimelinePeriod): string {
  if (period.coverageAmount !== null && Number.isFinite(period.coverageAmount)) {
    return formatCurrency(period.coverageAmount);
  }
  if (period.coverageAmountRaw) return period.coverageAmountRaw;
  return '—';
}

/** Federal minimum for this span's coverage family, e.g. 49 CFR 387.9 BIPD tiers. Not set for CARGO/BOND. */
function formatRequiredAmount(period: InsuranceTimelinePeriod): string {
  return period.requiredCoverageAmount !== null ? formatCurrency(period.requiredCoverageAmount) : '—';
}

function formatText(value: string | null): string {
  return value === null || value === '' ? '—' : value;
}

/** Row-level coverage status: lapsed gap (red), open policy (green), ended (yellow). */
function statusBadge(period: InsuranceTimelinePeriod): {
  label: string;
  classes: string;
  icon: 'gap' | 'active' | 'ended';
} {
  if (period.eventType === 'coverage_gap') {
    const days = period.gapDays !== null ? ` · ${period.gapDays} day${period.gapDays === 1 ? '' : 's'}` : '';
    return {
      label: `Coverage Lapsed${days}`,
      classes: 'bg-red-100 text-red-700',
      icon: 'gap',
    };
  }
  if (period.effectiveTo === null) {
    return { label: 'Active', classes: 'bg-green-100 text-green-700', icon: 'active' };
  }
  return { label: 'Ended', classes: 'bg-yellow-100 text-yellow-700', icon: 'ended' };
}

const StatusIcon: React.FC<{ icon: 'gap' | 'active' | 'ended' }> = ({ icon }) => {
  if (icon === 'gap') return <AlertTriangle size={12} aria-hidden="true" />;
  if (icon === 'active') return <CheckCircle2 size={12} aria-hidden="true" />;
  return <AlertCircle size={12} aria-hidden="true" />;
};

const LoadingSkeleton: React.FC = () => (
  <div className="animate-pulse space-y-3">
    {[0, 1, 2, 3].map((i) => (
      <div key={i} className="h-10 rounded bg-gray-100" />
    ))}
  </div>
);

/**
 * Table of a carrier's reconstructed insurance policy periods — underwriter,
 * policy number, coverage amount, effective and cancellation dates — with the
 * uncovered gaps between periods flagged in red. Backed by
 * GET /api/carrier-history/insurance/:dotNumber.
 */
const InsuranceHistoryTable: React.FC<InsuranceHistoryTableProps> = ({ dotNumber }) => {
  const { data, isLoading, error, refetch } = useCarrierHistory(dotNumber, 'insurance');

  const periods = data?.periods ?? [];
  const hasNoData = data !== null && (data.status !== 'ok' || periods.length === 0);
  const lapseCount = periods.filter((p) => p.eventType === 'coverage_gap').length;

  return (
    <div className="bg-white rounded-lg shadow-md border border-gray-200 p-4 sm:p-6">
      <div className="flex items-center justify-between gap-4 mb-4">
        <div className="flex items-center gap-2">
          <Shield size={20} className="text-gray-700" aria-hidden="true" />
          <h3 className="text-xl font-semibold text-gray-900">Insurance History</h3>
        </div>
        {!isLoading && !error && (
          <button
            type="button"
            onClick={() => refetch()}
            className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 border border-gray-300 rounded-md px-2.5 py-1.5 bg-white"
            aria-label="Refresh insurance history"
          >
            <RefreshCw size={14} aria-hidden="true" />
            Refresh
          </button>
        )}
      </div>

      {isLoading && <LoadingSkeleton />}

      {!isLoading && error && (
        <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4">
          <AlertCircle size={20} className="text-red-600 mt-0.5" aria-hidden="true" />
          <div>
            <p className="text-sm font-semibold text-red-700">
              Unable to load insurance history
            </p>
            <p className="text-sm text-red-600">{error}</p>
            <button
              type="button"
              onClick={() => refetch()}
              className="mt-2 inline-flex items-center gap-1.5 text-sm text-red-700 underline"
            >
              <RefreshCw size={14} aria-hidden="true" />
              Try again
            </button>
          </div>
        </div>
      )}

      {!isLoading && !error && hasNoData && (
        <div className="flex flex-col items-center gap-2 py-8 text-center">
          <Shield size={32} className="text-gray-300" aria-hidden="true" />
          <p className="text-base text-gray-700 font-medium">
            No insurance filings recorded
          </p>
          <p className="text-sm text-gray-500">
            {data?.reason ??
              'Insurance policy periods and coverage gaps will appear here once filings are recorded for this carrier.'}
          </p>
        </div>
      )}

      {!isLoading && !error && !hasNoData && (
        <>
          <p className="text-sm text-gray-600 mb-4">
            {periods.length} recorded period{periods.length === 1 ? '' : 's'} (oldest first)
            {lapseCount > 0 && (
              <span className="text-red-600 font-medium">
                {' · '}
                {lapseCount} coverage lapse{lapseCount === 1 ? '' : 's'} detected
              </span>
            )}
          </p>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Coverage
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Policy
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Underwriter
                  </th>
                  <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Required
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Effective
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Cancelled
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {periods.map((period, index) => {
                  const isGap = period.eventType === 'coverage_gap';
                  const badge = statusBadge(period);
                  return (
                    <tr
                      key={`${period.coverageFamily}-${period.policyKey}-${period.effectiveFrom}-${index}`}
                      className={isGap ? 'bg-red-50' : undefined}
                    >
                      <td className="px-3 py-2 text-sm font-medium text-gray-900">
                        {period.coverageFamily}
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-700">
                        {isGap || period.policyKey === 'UNKNOWN' ? '—' : period.policyKey}
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-700">
                        {isGap ? '—' : formatText(period.underwriter)}
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-700 text-right">
                        {isGap ? '—' : formatCoverageAmount(period)}
                      </td>
                      <td
                        className="px-3 py-2 text-sm text-gray-700 text-right"
                        title={period.requiredCoverageBasis ?? undefined}
                      >
                        {isGap ? '—' : formatRequiredAmount(period)}
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-700">
                        {formatDate(period.effectiveFrom)}
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-700">
                        {period.effectiveTo ? formatDate(period.effectiveTo) : '—'}
                      </td>
                      <td className="px-3 py-2 text-sm">
                        <span
                          className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium ${badge.classes}`}
                        >
                          <StatusIcon icon={badge.icon} />
                          {badge.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {data !== null && (data.skippedFilings > 0 || data.unparseableDates > 0) && (
            <p className="text-xs text-gray-500 mt-3">
              {data.skippedFilings > 0 &&
                `${data.skippedFilings} filing${data.skippedFilings === 1 ? '' : 's'} skipped due to unusable effective dates. `}
              {data.unparseableDates > 0 &&
                `${data.unparseableDates} source date${data.unparseableDates === 1 ? '' : 's'} could not be parsed.`}
            </p>
          )}
        </>
      )}
    </div>
  );
};

export default InsuranceHistoryTable;
